import { useContext, useEffect, useState } from 'react';
import { Field } from '../../components/field/field';
import { useRepresentativeApi } from '../../clients/quipli-service/representatives/representative-api';
import { toast } from '../../util/toasts';
import { useAPI } from '../../clients/quipli-service/quipli-service-client';
import { EmailTemplate } from '../../clients/quipli-service/email/email';
import { Dictionary, groupBy } from 'lodash';
import {
  EmailTemplateCategory,
  EmailTemplateItem,
  EmailTemplateList,
  EmailTemplateWrapper,
} from '../settings/email-templates/EmailTemplates';
import { Card } from '../../components/card/card';
import { useNavigate, useParams } from 'react-router-dom';
import { Checkbox } from '../../components/checkbox/checkbox';
import { Typography } from '../../components/typography/typography';
import SkeletonLoader from '../../components/skeleton-loader/skeleton-loader';
import { RepresentativeRole, UpsertRoleRequest } from '../../clients/quipli-service/representatives/representative';
import { TenantContext } from '../../context/tenant-context';
import { Spinner } from '../../components/spinner/spinner';

export const RepresentativeRoleView = () => {
  const { roleId } = useParams();
  const { contextPath } = useContext(TenantContext);
  const { getRole, updateRole } = useRepresentativeApi();
  const navigate = useNavigate();
  const navigateToRoles = () => navigate(`${contextPath}manager/representatives/roles`);

  const [isLoading, setLoading] = useState<boolean>(true);
  const [role, setRole] = useState<RepresentativeRole>();

  const init = async () => {
    if (!roleId) {
      navigateToRoles();
      toast('Invalid role id', 'error');
      return;
    }

    try {
      const role = await getRole(roleId);
      setRole(role);
    } catch (e: any) {
      toast(e.message.msg ?? e.message ?? 'Failed to fetch role', 'error');
      navigateToRoles();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    init();
  }, []);

  return isLoading && !role ? (
    <Spinner />
  ) : (
    <RoleForm
      role={role}
      onSubmit={async (req) => {
        if (!roleId) return;

        await updateRole(roleId, req);
        toast('Role updated successfully', 'success');
        navigate(`${contextPath}manager/representatives/roles`);
      }}
    />
  );
};

export const CreateRepresentativeRole = () => {
  const { createRole } = useRepresentativeApi();
  const navigate = useNavigate();

  return (
    <RoleForm
      onSubmit={async (req) => {
        await createRole(req);
        toast('Role created successfully', 'success');
        navigate(-1);
      }}
    />
  );
};

const RoleForm = ({
  role,
  onSubmit,
}: {
  role?: RepresentativeRole;
  onSubmit: (req: UpsertRoleRequest) => Promise<void>;
}) => {
  const { searchEmailTemplates } = useAPI();

  // React states
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isRequestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [name, setName] = useState<string>(role?.name || '');
  const [emailTemplates, setEmailTemplates] = useState<Dictionary<EmailTemplate[]>>({});
  const [categoriesToOpen, setCategoriesToOpen] = useState<Record<string, boolean>>({});
  const [selectedTemplates, setSelectedTemplates] = useState<Map<string, boolean>>(
    new Map(role?.emailTemplates.map((t) => [t.emailTemplateName, true]) || []),
  );
  const isDisabled = name.length === 0;

  const handleSubmit = async () => {
    if (isRequestInProgress || isDisabled) {
      return;
    }

    setRequestInProgress(true);
    await onSubmit({ name, emailTemplateNames: Array.from(selectedTemplates.keys()) });
    setRequestInProgress(false);
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      // Templates
      const et = await searchEmailTemplates('Customer');
      const grouped = groupBy(et, 'categoryName');
      setEmailTemplates(grouped);
      setCategoriesToOpen(Object.keys(grouped).reduce((acc, key) => ({ ...acc, [key]: false }), {}));
    } catch (e: any) {
      toast(e.message.msg ?? e.message ?? 'Failed to fetch email templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelection = (templateName: string, isSelected: boolean) => {
    setSelectedTemplates((prev) => {
      const newMap = new Map(prev);
      if (isSelected) {
        newMap.set(templateName, true);
      } else {
        newMap.delete(templateName);
      }
      return newMap;
    });
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  return (
    <div className="px-[10px] pb-[5px]">
      <Typography type="display-sm" weight="medium">
        {role ? 'Edit' : 'Create'} Role
      </Typography>

      <div className="pt-5">
        <Card className="flex flex-col gap-3 p-2 sm:px-6">
          {isLoading ? (
            <RepresentativeRoleFormSkeleton />
          ) : (
            <>
              <Field
                type="text"
                label="Role"
                dataCy="role"
                value={name}
                onChange={(name: string) => setName(name)}
                isRequired={true}
              />

              <Typography type="text-sm">{selectedTemplates.size} email templates selected</Typography>

              <EmailTemplateWrapper>
                <EmailTemplateList>
                  {Object.entries(emailTemplates)
                    .filter(([, templates]) => templates.length > 0)
                    .map(([category, templates]) => (
                      <EmailTemplateCategory
                        isOpen={categoriesToOpen[category]}
                        category={category}
                        onClick={() => setCategoriesToOpen((prev) => ({ ...prev, [category]: !prev[category] }))}
                        key={`category-${category}`}
                      >
                        {templates.map((template) => (
                          <EmailTemplateItem
                            key={`template-${template.id}`}
                            template={template}
                            onClick={() => {
                              handleTemplateSelection(template.name, !selectedTemplates.get(template.name));
                            }}
                          >
                            <Checkbox
                              isChecked={selectedTemplates.get(template.name) || false}
                              onChange={(isChecked) => handleTemplateSelection(template.name, isChecked)}
                            />
                          </EmailTemplateItem>
                        ))}
                      </EmailTemplateCategory>
                    ))}
                </EmailTemplateList>
              </EmailTemplateWrapper>
            </>
          )}

          <div className="flex justify-end">
            <button className="button-yellow" disabled={isRequestInProgress || isDisabled} onClick={handleSubmit}>
              {isRequestInProgress && <Spinner />} Save
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
};

const RepresentativeRoleFormSkeleton = () => {
  return (
    <>
      <SkeletonLoader style={{ width: '10%', height: '20px' }} />
      <SkeletonLoader style={{ width: '100%', height: '40px' }} />
      <SkeletonLoader style={{ width: '33%', height: '20px' }} />
      <div className="flex flex-col mt-4 gap-8">
        <SkeletonLoader style={{ width: '100%', height: '60px' }} />
        <SkeletonLoader style={{ width: '100%', height: '60px' }} />
        <SkeletonLoader style={{ width: '100%', height: '60px' }} />
        <SkeletonLoader style={{ width: '100%', height: '60px' }} />
        <SkeletonLoader style={{ width: '100%', height: '60px' }} />
      </div>
    </>
  );
};
